import React from "react";
import PropTypes from "prop-types";
import { Row, Col } from "react-flexbox-grid";
import { Table, Column, Cell } from "fixed-data-table-2";
import {
  AppBar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Toolbar,
  Typography
} from "@material-ui/core";
import Close from "@material-ui/icons/Close";
import moment from "moment";
import { isSeabird } from "util/seabird";

const TIME_DIFF_WARN_S = 3 * 60;
const TIME_DIFF_ERR_S = 6 * 60;
const DEPTH_DIFF_WARN_M = 5;
const DEPTH_DIFF_ERR_M = 10;

const styles = {
  textInputStyle: {
    width: "100%",
    background: "transparent",
    border: "none"
  },
  warnDark: {
    backgroundColor: "hsl(40, 100%, 89%)"
  },
  warnLight: {
    backgroundColor: "hsl(40, 100%, 93%)"
  },
  errorDark: {
    backgroundColor: "hsl(359, 87%, 89%)"
  },
  errorLight: {
    backgroundColor: "hsl(359, 87%, 93%)"
  },
  tableFont: {
    fontFamily: "Roboto",
    fontSize: "14px"
  }
};

const getTimeCellStyle = (castTime, formTime, rowIndex) => {
  const timeDeltaMs = Math.abs(moment(castTime).diff(moment(formTime)));
  if (timeDeltaMs > TIME_DIFF_ERR_S * 1000) {
    return rowIndex % 2 === 0 ? styles.errorLight : styles.errorDark;
  }
  if (timeDeltaMs > TIME_DIFF_WARN_S * 1000) {
    return rowIndex % 2 === 0 ? styles.warnLight : styles.warnDark;
  }
  return {};
};

const getDepthCellStyle = (castDepth, formDepth, rowNum) => {
  const depthDelta = Math.abs(formDepth - castDepth);
  if (depthDelta > DEPTH_DIFF_ERR_M) {
    return rowNum % 2 === 0 ? styles.errorLight : styles.errorDark;
  }
  if (depthDelta > DEPTH_DIFF_WARN_M) {
    return rowNum % 2 === 0 ? styles.warnLight : styles.warnDark;
  }
  return {};
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return (
    <Slide direction="up" ref={ref} {...props} mountOnEnter unmountOnExit />
  );
});

const AnnotationModal = props => (
  <Dialog
    fullScreen
    open={props.annotationModalOpen}
    onClose={props.onAnnotationModalClose}
    TransitionComponent={Transition}
  >
    <AppBar>
      <Toolbar>
        <IconButton onClick={props.onAnnotationModalClose} color="inherit">
          <Close />
        </IconButton>

        <Typography variant="h6" style={{ color: "#fff" }}>
          Change form
        </Typography>
      </Toolbar>
    </AppBar>

    <DialogTitle style={{ marginTop: 75, fontSize: 60 }}>
      Select the form that matches the instrument data
    </DialogTitle>
    <DialogContent>
      <Row center="xs" style={styles.tableFont}>
        <Col xs={12}>
          <h3>Instrument Data:</h3>
        </Col>
        <Table
          rowsCount={1}
          rowHeight={30}
          headerHeight={30}
          width={1200}
          height={62}
        >
          <Column
            header="Date"
            cell={({ ...colProps }) => (
              <Cell {...colProps}>
                {moment(props.annotationModalCast.startDt).format("ll")}
              </Cell>
            )}
            width={110}
            flexGrow={1}
          />
          <Column
            header="Start"
            cell={({ ...colProps }) => {
              const dt = moment(props.annotationModalCast.startDt);
              return (
                <Cell {...colProps} title={props.annotationModalCast.startDt}>
                  {dt.format("LT")}
                </Cell>
              );
            }}
            width={77}
            flexGrow={1}
          />
          <Column
            header="Bottom"
            cell={({ ...colProps }) => {
              const dt = moment(props.annotationModalCast.bottomDt);
              return (
                <Cell {...colProps} title={props.annotationModalCast.bottomDt}>
                  {dt.format("LT")}
                </Cell>
              );
            }}
            width={77}
            flexGrow={1}
          />
          <Column
            header="Depth"
            cell={({ rowIndex, ...colProps }) => {
              const depth = props.annotationModalCast.bottomDepth
                ? `${Math.round(props.annotationModalCast.bottomDepth * 10) /
                    10} m`
                : "-";
              return (
                <Cell {...colProps} title={depth}>
                  {depth}
                </Cell>
              );
            }}
            width={90}
            flexGrow={1}
          />
        </Table>
      </Row>

      <Row center="xs" style={{ ...styles.tableFont, marginTop: "2rem" }}>
        <Col xs={12}>
          <h3>Select the correct form:</h3>
        </Col>
        <Table
          rowsCount={props.annotationModalForms.length}
          rowHeight={30}
          headerHeight={30}
          width={1200}
          height={props.annotationModalForms.length * 30 + 32}
          onRowClick={props.onAnnotationRowSelect}
        >
          <Column
            cell={({ rowIndex, ...colProps }) => (
              <Cell {...colProps}>
                <input
                  type="radio"
                  checked={rowIndex === props.annotationModalSelected}
                  onChange={() => {}}
                />
              </Cell>
            )}
            width={35}
            flexGrow={1}
          />
          <Column
            header="Date"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.annotationModalForms[rowIndex];
              const dt = moment(form.dropStartTime);
              return (
                <Cell {...colProps} title={form.dropStartTime}>
                  {dt.format("ll")}
                </Cell>
              );
            }}
            width={110}
            flexGrow={1}
          />
          <Column
            header="Start"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.annotationModalForms[rowIndex];
              const dt = moment(form.dropStartTime);
              const style = getTimeCellStyle(
                props.annotationModalCast.startDt,
                form.dropStartTime,
                rowIndex
              );
              return (
                <Cell {...colProps} style={style} title={dt.format()}>
                  {dt.format("LT")}
                </Cell>
              );
            }}
            width={77}
            flexGrow={1}
          />
          <Column
            header="Bottom"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.annotationModalForms[rowIndex];
              if (form.dropBottomTime == null) {
                return <Cell {...colProps}>N/A</Cell>;
              }
              const dt = moment(form.dropBottomTime);
              const style = getTimeCellStyle(
                props.annotationModalCast.bottomDt,
                form.dropBottomTime,
                rowIndex
              );
              return (
                <Cell {...colProps} style={style} title={dt.format()}>
                  {dt.format("LT")}
                </Cell>
              );
            }}
            width={77}
            flexGrow={1}
          />
          <Column
            header="End"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.annotationModalForms[rowIndex];
              if (form.dropEndTime == null) {
                return <Cell {...colProps}>N/A</Cell>;
              }
              const dt = moment(form.dropEndTime);
              return (
                <Cell {...colProps} title={dt.format()}>
                  {dt.format("LT")}
                </Cell>
              );
            }}
            width={77}
            flexGrow={1}
          />
          <Column
            header="Survey"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.annotationModalForms[rowIndex];
              return (
                <Cell {...colProps} title={form.survey}>
                  {form.survey}
                </Cell>
              );
            }}
            width={90}
            flexGrow={1}
          />
          <Column
            header="Site"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.annotationModalForms[rowIndex];
              return (
                <Cell {...colProps} title={form.siteId}>
                  {form.siteId}
                </Cell>
              );
            }}
            width={90}
            flexGrow={1}
          />
          <Column
            header="Site dist."
            cell={({ rowIndex, ...colProps }) => {
              const form = props.annotationModalForms[rowIndex];
              const dist = form.distanceFromSampleSite;
              return <Cell {...colProps}>{dist && `${dist}m`}</Cell>;
            }}
            width={100}
            flexGrow={1}
          />
          <Column
            header="Target depth"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.annotationModalForms[rowIndex];
              const style = isSeabird(props.annotationModalCast.filename)
                ? {}
                : getDepthCellStyle(
                    props.annotationModalCast.bottomDepth,
                    form.targetDepthM,
                    rowIndex
                  );
              return (
                <Cell
                  {...colProps}
                  style={style}
                >{`${form.targetDepthM}m`}</Cell>
              );
            }}
            width={100}
            flexGrow={1}
          />
          <Column
            header="Drop"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.annotationModalForms[rowIndex];
              return <Cell {...colProps}>{form.drop}</Cell>;
            }}
            width={50}
            flexGrow={1}
          />
          <Column
            header="Miscast"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.annotationModalForms[rowIndex];
              const { miscast } = form;
              return (
                <Cell {...colProps}>
                  <input
                    type="checkbox"
                    disabled
                    checked={miscast}
                    onChange={() => {}}
                  />
                </Cell>
              );
            }}
            width={70}
            flexGrow={2}
          />
          <Column
            header="Crew"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.annotationModalForms[rowIndex];
              return (
                <Cell {...colProps} title={form.fieldStaff}>
                  {form.fieldStaff || ""}
                </Cell>
              );
            }}
            width={100}
          />
          <Column
            header="Comments"
            cell={({ rowIndex, ...colProps }) => {
              const form = props.annotationModalForms[rowIndex];
              return (
                <Cell {...colProps} title={form.qualityLog}>
                  {form.qualityLog || ""}
                </Cell>
              );
            }}
            width={160}
            flexGrow={2}
          />
        </Table>
      </Row>
    </DialogContent>
    <DialogActions>
      <Button variant="text" onClick={props.onAnnotationModalClose}>
        Close
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={props.onAnnotationUpdateClick}
        disabled={props.annotationModalSelected == null}
      >
        Update
      </Button>
    </DialogActions>
  </Dialog>
);

const formShape = PropTypes.shape({
  dropStartTime: PropTypes.string,
  dropBottomTime: PropTypes.string,
  dropEndTime: PropTypes.string,
  survey: PropTypes.string,
  siteId: PropTypes.string,
  distanceFromSampleSite: PropTypes.number,
  targetDepthM: PropTypes.number,
  drop: PropTypes.number,
  miscast: PropTypes.bool,
  fieldStaff: PropTypes.string,
  qualityLog: PropTypes.string
});

const castShape = PropTypes.shape({
  ctdFilePk: PropTypes.number,
  ctdCastPk: PropTypes.number,
  startDt: PropTypes.string,
  bottomDt: PropTypes.string,
  matchingForm: formShape,
  possibleForms: PropTypes.arrayOf(formShape),
  bottomDepth: PropTypes.number,
  filename: PropTypes.string
});

AnnotationModal.propTypes = {
  onAnnotationModalClose: PropTypes.func.isRequired,
  onAnnotationRowSelect: PropTypes.func.isRequired,
  onAnnotationUpdateClick: PropTypes.func.isRequired,
  annotationModalCast: castShape,
  annotationModalForms: PropTypes.arrayOf(formShape),
  annotationModalOpen: PropTypes.bool,
  annotationModalSelected: PropTypes.number,
  annotationModalUpdateDisabled: PropTypes.bool
};

AnnotationModal.defaultProps = {
  annotationModalUpdateDisabled: false,
  annotationModalOpen: false,
  annotationModalCast: {},
  annotationModalForms: [],
  annotationModalSelected: null
};

export default AnnotationModal;
